import React from 'react';
import { pricingPlans } from '../data/mockData';
import { Button } from './ui/button';
import { Check, Star } from 'lucide-react';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';

const PricingSection = ({ onOpenContactForm }) => {
  return (
    <section id="pricing" className="relative py-20 bg-gradient-to-br from-[#1a1a2e] via-gray-900 to-[#1a1a2e] overflow-hidden">
      {/* Wave Divider Top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none rotate-180">
        <svg className="relative block w-full h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-gray-900"></path>
        </svg>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-72 h-72 bg-[#FF9900] rounded-full opacity-5 blur-3xl"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-blue-500 rounded-full opacity-5 blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Heading */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-block bg-gradient-to-r from-[#FF9900]/20 to-orange-600/20 border border-[#FF9900]/30 text-[#FF9900] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Pricing Plans
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Affordable Trademark Registration Pricing | Starting at $45
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            Transparent trademark registration services with online trademark filing packages. Our trademark attorneys provide federal trademark protection, USPTO trademark application filing, trademark search services, and Amazon Brand Registry enrollment support with affordable pricing starting at $45 plus federal filing fees.
          </p>
        </div>

        {/* Pricing Grid - Uniform Height */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-3xl shadow-2xl hover:shadow-[#FF9900]/20 transition-all duration-300 transform hover:-translate-y-2 border-2 flex flex-col ${
                plan.recommended ? 'border-[#FF9900] shadow-[#FF9900]/30' : 'border-white/10'
              }`}
              style={{ height: '650px' }}
            >
              {/* Recommended Badge */}
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-[#FF9900] to-orange-600 text-black px-4 py-1.5 text-sm font-bold">
                    <Star className="w-3 h-3 mr-1 inline fill-black" />
                    RECOMMENDED
                  </Badge>
                </div>
              )}

              {/* Plan Header */}
              <div className="p-6 text-center border-b border-white/10">
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <div className="mb-2">
                  <span className="text-4xl font-bold bg-gradient-to-r from-[#FF9900] to-orange-500 bg-clip-text text-transparent">{plan.price}</span>
                </div>
                <p className="text-sm text-gray-400">{plan.federal}</p>
              </div>

              {/* Features List - Scrollable */}
              <div className="flex-1 p-6 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <Check className="w-5 h-5 text-[#FF9900] mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
              </div>

              {/* Footer - Aligned at Bottom */}
              <div className="p-6 border-t border-white/10 mt-auto">
                <p className="text-xs text-gray-500 text-center mb-4">{plan.note}</p>
                <Button
                  onClick={onOpenContactForm}
                  className={`w-full font-semibold ${
                    plan.recommended
                      ? 'bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {plan.buttonText}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center max-w-3xl mx-auto">
          <p className="text-gray-400">
            <strong className="text-[#FF9900]">Note:</strong> All packages exclude USPTO federal filing fees ($250-$350 per class). These government fees are paid directly to the USPTO.
          </p>
        </div>
      </div>

      {/* Wave Divider Bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
        <svg className="relative block w-full h-20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z" className="fill-gray-900"></path>
        </svg>
      </div>
    </section>
  );
};

export default PricingSection;